import { Chip, Icon, IconToggleButton, LinearProgress, Popup, Text } from "@evergis/ui";
import styled from "styled-components";
import { DraggableList } from "../../../../../../components/DraggableList/DraggableList";

import { getFileTypeIcon } from "./getFileTypeIcon";

import { FileType } from "./types";

export const AttachmentsContainer = styled.div<{ isEdit?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-left: ${({ isEdit }) => (isEdit ? "0.5rem" : 0)};
`;

export const AttachmentsHeader = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

export const AttachmentsLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0 0.5rem;
`;

export const AttachmentsViewControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0 0.75rem;
  margin-right: 0.25rem;

  ${IconToggleButton} {
    width: 14px;
    height: 14px;
  }

  ${Icon} {
    width: 14px;
    height: 14px;
  }

  ${Icon}:after {
    font-size: 14px;
  }
`;

export const AttachmentsChip = styled(Chip)`
  padding: 2px 6px;

  ${Text} {
    font-size: 10px;
  }
`;

export const AttachmentsDate = styled.div`
  color: ${({ theme }) => theme.palette.textSecondary};
`;

export const AttachmentsContent = styled.div``;

export const GridList = styled(DraggableList)`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

export const GridActions = styled.div`
  display: none;
  position: absolute;
  z-index: 2;
  top: 0.25rem;
  right: 0;
  align-items: center;
  gap: 0 0.5rem;

  ${Icon}:after {
    color: ${({ theme }) => theme.palette.elementLight};
  }

  ${Icon}:hover {
    &:after {
      color: white;
    }
  }
`;

export const GridItemOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 4.5rem;
  height: 4.5rem;
  z-index: 1;
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.smallest};
`;

export const GridItemContainer = styled.div`
  display: flex;
  position: relative;
  width: 4.5rem;
  height: 4.5rem;
  cursor: pointer;
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.smallest};
  background-color: ${({ theme }) => theme.palette.elementDark};
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${({ theme }) => theme.palette.elementDeep};

    ${GridActions} {
      display: flex;
    }

    ${Icon} {
      opacity: 1;
    }

    ${GridItemOverlay} {
      background-color: rgba(0, 0, 0, 0.28);
    }
  }
`;

export const GridIcon = styled.div<{ fileType?: FileType }>`
  height: 2rem;
  width: 2rem;
  background: url(${({ fileType }) => getFileTypeIcon(fileType)}) center center no-repeat;
`;

export const ListIcon = styled.div<{ fileType?: FileType }>`
  height: 1.5rem;
  width: 1.5rem;
  background: url(${({ fileType }) => getFileTypeIcon(fileType)}) center center no-repeat;
`;

export const GridImagePreview = styled.img<{
  size?: string;
  height?: number;
  width?: number;
  borderRadius: string;
  isLoading?: boolean;
}>`
  display: flex;
  height: ${({ size = "4.5rem", height }) => (height ? `${height}px` : size)};
  width: ${({ size = "4.5rem", width }) => (width ? `${width}px` : size)};
  cursor: pointer;
  border-radius: ${({ theme: { borderRadius: themeBorder }, borderRadius }) => borderRadius || themeBorder.smallest};
  object-fit: cover;
  outline: none;
  visibility: ${({ isLoading }) => (isLoading ? "hidden" : "visible")};
`;

export const ShowMoreButton = styled.div`
  color: ${({ theme }) => theme.palette.primary};
  font-size: 0.75rem;
  margin-top: 0.75rem;
  cursor: pointer;
`;

export const AttachmentsListContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.5rem 0;
`;

export const AttachmentsAddButtonContainer = styled.div`
  display: flex;
  width: 100%;
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.smallest};
  background-color: ${({ theme }) => theme.palette.elementDark};
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.palette.icon};
  font-size: 14px;

  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.palette.elementDeep};

    ${Icon} {
      opacity: 1;
    }
  }
`;

export const AddButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

export const ListItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0 0.5rem;
  width: 100%;
  min-width: 1px;
`;

export const ListItemDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px 0;
  width: 100%;
  min-width: 1px;
`;

export const ListItemName = styled.div`
  font-size: 14px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const ListItemDate = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.palette.textSecondary};
`;

export const ListActions = styled.div`
  flex-shrink: 0;
`;

export const ListItemContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  &:hover {
    ${ListItemName} {
      color: ${({ theme }) => theme.palette.primary};
    }
  }
`;

export const ImagePreviewContainer = styled.div<{ size?: string; height?: number; width?: number }>`
  display: flex;
  flex-direction: column;
  position: relative;
  height: ${({ size = "4.5rem", height }) => (height ? `${height}px` : size)};
  width: ${({ size = "4.5rem", width }) => (width ? `${width}px` : size)};
`;

export const ImagePreviewLoaderContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 1.5rem);
  max-width: 6rem;
  box-sizing: border-box;

  ${LinearProgress} {
    border-radius: 0.25rem;
  }
`;

export const StyledMenuPopup = styled(Popup)`
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
`;
